import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, CheckCircle, Users, Award } from 'lucide-react';
import SEO from '../../components/SEO';

const PermanentRecruitment = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: Users,
      title: 'Cultural Fit',
      description: 'We match candidates who align with your company values and culture',
    },
    {
      icon: Award,
      title: 'Quality Guarantee',
      description: 'Comprehensive screening ensures only top talent reaches you',
    },
    {
      icon: CheckCircle,
      title: 'Long-term Success',
      description: 'Post-placement support to ensure successful integration',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Requirements Analysis',
      description: 'Deep dive into your technical needs, team dynamics, and company culture',
    },
    {
      step: '02',
      title: 'Candidate Sourcing',
      description: 'Leverage our network and recruitment platforms to find perfect matches',
    },
    {
      step: '03',
      title: 'Screening & Interviews',
      description: 'Rigorous technical assessments, cultural fit evaluation, and background checks',
    },
    {
      step: '04',
      title: 'Offer & Onboarding',
      description: 'Support through offer negotiation and smooth onboarding process',
    },
  ];

  const features = [
    'Comprehensive candidate screening process',
    'Technical skills evaluation and testing',
    'Cultural fit and behavioral assessment',
    'Background verification and reference checks',
    'Salary benchmarking and negotiation support',
    'Post-placement support and follow-up',
    'Replacement guarantee within probation period',
    'Industry-specific expertise and insights',
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Permanent IT Recruitment Services"
        description="Find full-time committed IT professionals who match your company culture and technical needs. Expert permanent recruitment solutions for building lasting technology teams."
        keywords="permanent IT recruitment, full-time hiring, IT talent acquisition, permanent tech jobs, IT recruitment agency, hire developers, hire engineers"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-slate-200">
              <span className="text-sm font-medium text-slate-700">Permanent Recruitment</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Build lasting teams with{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                the right talent
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Find full-time, committed technology professionals who match your company culture and technical requirements. We help you build teams that drive long-term success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
              >
                <span>Start Hiring</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <a
                href="tel:+91-9121711433"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-slate-900 rounded-full border-2 border-slate-200 hover:border-slate-300 transition-all duration-200 font-semibold text-lg"
              >
                <span>Call: +91-9121711433</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why permanent recruitment matters
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Building a successful team goes beyond technical skills—it's about finding the right fit
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our recruitment process
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A proven approach to finding and placing exceptional IT talent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                <div className="text-5xl font-bold text-[#f9a825]/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-12 rounded-3xl shadow-2xl">
              <Target className="w-32 h-32 text-white mx-auto opacity-30" />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                What's included
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our comprehensive permanent recruitment service covers every aspect of finding and placing the right candidate.
              </p>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#f9a825] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-0 transition-opacity duration-1000 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to build your dream team?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's find the perfect permanent employees for your organization
          </p>
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
          >
            <span>Contact Us Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PermanentRecruitment;
